import React from 'react';
import { useHistory } from 'react-router-dom';
import { StepComponent } from '@service-victoria/component-library/components/Wizard/props';
import Text from '@service-victoria/component-library/components/Text';
import Accordion from '@service-victoria/component-library/components/Accordion';
import Notification from '@service-victoria/component-library/components/Notification';
import Checkbox from '@service-victoria/component-library/components/Checkbox';
import { constants } from 'utils';
import * as Interfaces from 'interfaces';
import * as Styled from '../styled';
import HtmlText from 'Components/HtmlText';
import { getStreetAddress } from '../../services/permitService/helper';
import exemptionReasons from '../Assess/exemption-reasons';
import {
  isExemptionApplication,
  isOrangeZoneResident,
  isRedZoneResident,
  isSpecifiedWorker
} from '../Assess/travel-reasons';
import { useValidatePreviousStep } from 'hooks';
import dayjs from 'dayjs';
import { isCrossBorderResident } from '../Assess/travel-reasons';
import { CovidAddress } from 'interfaces/common';

export type ComponentProps = StepComponent<Partial<Interfaces.Common.FormValues>>;

const Detail = ({
  name,
  value,
  dataTestId
}: {
  name: string;
  value: string;
  dataTestId?: string;
}) => (
  <Text.Body style={{ wordBreak: 'break-word' }}>
    {name}:{' '}
    <strong data-testid={dataTestId ?? `${name.replace(/ /g, '-')}-DetailsValue`}>{value}</strong>
  </Text.Body>
);

const TravelDetailsAddressReview: React.FC<{
  address: CovidAddress;
  dataTestId: string;
  formatStreetAddress?: boolean;
}> = ({ address, dataTestId, formatStreetAddress }) => {
  return address.isOverseasAddress ? (
    <>
      <Detail
        dataTestId={`${dataTestId}Country-DetailsValue`}
        name='Country'
        value={address.overseas.country}
      />
      <Detail
        dataTestId={`${dataTestId}Address-DetailsValue`}
        name='Address'
        value={address.overseas.info.basic}
      />
    </>
  ) : (
    <>
      <Detail
        dataTestId={`${dataTestId}StreetAddress-DetailsValue`}
        name='Street address'
        value={
          formatStreetAddress
            ? getStreetAddress(address.fullDetails.fullAddress)
            : address.fullDetails.fullAddress
        }
      />
      <Detail
        dataTestId={`${dataTestId}Suburb-DetailsValue`}
        name='Suburb'
        value={address.fullDetails.suburb}
      />
    </>
  );
};

const Review: React.FC<ComponentProps> = ({ context }) => {
  const { values: vRaw } = context.form;
  const values = vRaw as Interfaces.Common.FormValues;

  useValidatePreviousStep('review', context);

  const history = useHistory();
  const { currentState } = context.appStates[
    constants.StorageKeys.State
  ] as Interfaces.Store.AppContext;
  const { travelReasons } = currentState;
  const [errorCount, setErrorCount] = React.useState(0);
  // Validate that user filled previous step forms

  React.useEffect(() => {
    if (currentState.error) {
      setErrorCount(errorCount => errorCount + 1);
    }
  }, [currentState.error]);

  React.useEffect(() => {
    window.scrollTo(0, 70);

    if (errorCount >= 3) {
      history.push(constants.Routes.TechnicalError);
    }
  }, [errorCount, history]);

  const targetPlace = values.assess.Depart.homeAddressIsDestination
    ? 'Home'
    : values.assess.Depart.targetPlace;

  const targetAddress = values.assess.Depart.homeAddressIsDestination
    ? values.assess.Depart.homeAddress
    : values.assess.Depart.target;

  // Get list of reason text from state based on the codes retrieved above
  const travelReason =
    travelReasons.status === 'success'
      ? travelReasons.reasons.find(reason => reason.value === values.assess.reasons)
      : undefined;
  const reason = travelReason?.label;
  const reasonCode = travelReason?.value;
  const me = `${values.identity.givenName} ${values.identity.familyName}`;

  const attestation = getAttestation(travelReason, reasonCode, values.assess.vaccinationStatus);

  const homeAddressKey =
    values.assess.Depart.from === 'other' || isCrossBorderResident(reasonCode)
      ? 'homeAddress'
      : 'fromAddress';

  return (
    <Styled.Wrapper>
      {!!currentState.error && (
        <Styled.Error>
          <Notification.Error.Text
            title={currentState.error?.title ?? ''}
            value={currentState.error?.value ?? ''}
          />
          <Styled.Spacer48 />
        </Styled.Error>
      )}
      <Styled.InverseMarginMobile>
        <Accordion
          items={[
            {
              id: 'your-details',
              title: <div data-testid='YourDetails-Accordion'>Your details</div>,
              component: (
                <React.Fragment>
                  <Detail name='Given name' value={values.identity.givenName} />
                  <Detail name='Family name' value={values.identity.familyName} />
                  <Detail name='Mobile number' value={values.identity.phone} />
                  <Detail name='Your email' value={values.identity.email} />
                  <Detail
                    name='Who is travelling'
                    value={
                      values.identity.whoIsTravelling === 'Me'
                        ? `${me}`
                        : `${me}, ${values.identity.othersTravel
                            ?.map(other => other.name)
                            .join(', ')}`
                    }
                    data-testid='Who-is-travelling-DetailsValue'
                  />
                </React.Fragment>
              )
            },
            {
              id: 'travel-details',
              title: <div data-testid='TravelDetails-Accordion'>Travel details</div>,
              component: (
                <React.Fragment>
                  <Detail name='Vaccination status' value={values.assess.vaccinationStatus} />
                  <Detail name='Reason' value={reason || ''} />
                  <Detail name='Date of entry' value={formatDate(values.assess.Depart.date)} />
                  {!isCrossBorderResident(reasonCode) && (
                    <>
                      <Styled.Spacer24 />
                      <Text.Heading.H3 fontWeight={600}>Leaving from</Text.Heading.H3>
                      <TravelDetailsAddressReview
                        address={values.assess.Depart.fromAddress}
                        dataTestId='LeavingFrom'
                      />
                      {values.assess.Depart.quarantine.fromQuarantine && (
                        <Detail
                          name='Quarantine hotel'
                          value={values.assess.Depart.quarantine.hotel}
                        />
                      )}
                    </>
                  )}
                  <Styled.Spacer24 />
                  {values.assess.Depart[homeAddressKey] && (
                    <>
                      <Text.Heading.H3 fontWeight={600}>Home address</Text.Heading.H3>
                      <TravelDetailsAddressReview
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        address={values.assess.Depart[homeAddressKey]!}
                        dataTestId='Home'
                      />
                    </>
                  )}
                  {!isCrossBorderResident(reasonCode) && (
                    <>
                      <Styled.Spacer24 />
                      <Text.Heading.H3 fontWeight={600}>Main destination</Text.Heading.H3>
                      <Detail
                        dataTestId='MainDestinationPlace-DetailsValue'
                        name='Place'
                        value={targetPlace}
                      />
                      <TravelDetailsAddressReview
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        address={targetAddress!}
                        dataTestId='MainDestination'
                      />
                    </>
                  )}
                  {isExemptionApplication(travelReason?.value.toString()) && (
                    <>
                      <Styled.Spacer24 />
                      <Text.Heading.H3 fontWeight={600}>Request for exemption</Text.Heading.H3>
                      <Detail
                        name='Exemption reason'
                        value={(values.assess.exemptionReason || [])
                          .map((item, i) => ({ index: i, ...item }))
                          .filter(item => item && item.selected)
                          .map(({ index }) => exemptionReasons[index].value)
                          .join(', ')}
                      />
                      <Detail name='Circumstances' value={values.assess.circumstances || ''} />
                      <Detail
                        name='Proof of residence'
                        value={
                          values.assess.hasDriverLicence === 'yes'
                            ? 'I have a driver licence'
                            : "I don't have a driver licence"
                        }
                      />
                      <Detail
                        name='Negative COVID-19 test in the last 3 days'
                        value={values.assess.negativeTest || ''}
                      />
                    </>
                  )}
                </React.Fragment>
              )
            }
          ]}
        />
      </Styled.InverseMarginMobile>
      <Styled.Spacer48 />
      <HtmlText data-testid='Attestation-Content'>{attestation}</HtmlText>
      <Styled.Spacer16 />
      <Checkbox
        name='consent'
        label={travelReason?.attestationDeclaration}
        data-testid='Attestation-CheckBox'
        errorLabelProps={{
          'data-testid': 'Attestation-CheckBoxError'
        }}
      />
      <HtmlText>{travelReason?.attestationPostDeclaration}</HtmlText>
    </Styled.Wrapper>
  );
};

function formatDate(date: string) {
  return dayjs(date).format('DD/MM/YYYY');
}

function getAttestation(
  travelReason: Interfaces.Common.TravelReason | undefined,
  reasonCode: string | undefined,
  vaccinationStatus: string
) {
  const isFullyVaccinated =
    vaccinationStatus === constants.vaxStatusFieldOptions.fullyVaccinated.value;

  if (
    isFullyVaccinated &&
    (isOrangeZoneResident(reasonCode) ||
      isRedZoneResident(reasonCode) ||
      isSpecifiedWorker(reasonCode))
  ) {
    return travelReason?.attestationFullyVax;
  }

  return travelReason?.attestation;
}

export default Review;
